import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast, toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import type {
    SpeedBackend,
    UntrackedOrder,
    UntrackedPosition,
} from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

import { useUntrackedExposure } from '../hooks/useUntrackedExposure';
import { getPriceConfig } from '../utils/priceFormat';

/** Plain decimal USD string (never base units) to `$12.34`. */
function fmtUsd(raw: string): string {
    const value = Number(raw);
    return Number.isFinite(value) ? `$${value.toFixed(2)}` : '$--';
}

/** A price string at the precision its own magnitude deserves. */
function fmtPrice(raw: string | null): string | null {
    if (raw === null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return null;
    return `$${getPriceConfig(value).formatDisplay(value)}`;
}

/** Trim the exchange's trailing zeros without lying about the size. */
function fmtSize(raw: string): string {
    const value = Number(raw);
    if (!Number.isFinite(value)) return raw;
    return String(Math.abs(value));
}

const SideChip: React.FC<{ label: string; negative: boolean }> = ({
    label,
    negative,
}) => (
    <span
        className={cn(
            'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
            negative
                ? 'bg-destructive/15 text-destructive'
                : 'bg-primary/15 text-primary',
        )}
    >
        {label}
    </span>
);

const ExposureRow: React.FC<{
    coin: string;
    side: string;
    negative: boolean;
    detail: string;
    amount: string;
    amountLabel: string;
    actionLabel: string;
    pending: boolean;
    disabled: boolean;
    onAction: () => void;
}> = ({
    coin,
    side,
    negative,
    detail,
    amount,
    amountLabel,
    actionLabel,
    pending,
    disabled,
    onAction,
}) => (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-amber-500/20 bg-background/40 px-2.5 py-2">
        <div className="flex min-w-0 flex-1 basis-[10rem] flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
                <span className="truncate font-mono text-[11px] font-semibold text-foreground">
                    {coin}
                </span>
                <SideChip label={side} negative={negative} />
            </div>
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                {detail}
            </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
            <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                {amountLabel}
            </span>
            <span className="font-mono text-[11px] font-semibold tabular-nums text-foreground">
                {amount}
            </span>
        </div>
        <Button
            variant="outline"
            size="sm"
            onClick={onAction}
            disabled={disabled}
            className="w-full shrink-0 gap-1.5 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10 sm:w-auto"
        >
            {pending ? (
                <Loader2 size={13} className="animate-spin" />
            ) : (
                <span className="text-[11px] font-bold uppercase">
                    {actionLabel}
                </span>
            )}
        </Button>
    </div>
);

function positionDetail(position: UntrackedPosition): string {
    const parts = [`${fmtSize(position.size)} @ `];
    const entry = fmtPrice(position.entryPx);
    parts[0] += entry ?? 'market';
    const liq = fmtPrice(position.liquidationPx);
    if (liq !== null) parts.push(`liq ${liq}`);
    if (position.leverage !== null) parts.push(`${position.leverage}x`);
    return parts.join(' · ');
}

function orderDetail(order: UntrackedOrder): string {
    const trigger = fmtPrice(order.triggerPx);
    const limit = fmtPrice(order.limitPx);
    const at = order.isTrigger ? (trigger ?? limit) : (limit ?? trigger);
    const parts = [`${fmtSize(order.size)}${at !== null ? ` @ ${at}` : ''}`];
    if (order.orderType) parts.push(order.orderType);
    if (order.reduceOnly) parts.push('reduce only');
    return parts.join(' · ');
}

/**
 * "Funds held outside your tracked trades."
 *
 * The incident this exists for: a partially filled GTC short left its remainder
 * resting, the remainder filled into a position no trade row covered, and the
 * user saw an empty terminal while ~$50 of their balance was held by the
 * exchange. The position was liquidated before anyone could act on it.
 *
 * So: whatever the server cannot match to a row is named here, with the one
 * move that frees it. Close goes through the ordinary `speed/trade/close`,
 * which sizes from the live position when no row exists; cancel drops a single
 * resting order by its Hyperliquid oid. Nothing here is classified client-side.
 *
 * Renders nothing at all when there is no untracked exposure, when the query is
 * disabled (demo mode, no session) or when the read failed — a panel that
 * cannot see the exchange has nothing honest to say.
 */
const UntrackedExposureCard: React.FC<{ className?: string }> = ({
    className,
}) => {
    const { data, isError } = useUntrackedExposure();
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    const networkId = useTokenPairStore((s) => s.networkId);
    // one key at a time: every action here changes the same list, so a second
    // request fired against a stale row is a request against something else.
    const [pendingKey, setPendingKey] = useState<string | null>(null);

    const refreshAfterAction = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.exposure(networkId ?? 0),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.active(networkId ?? 0),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.history(networkId ?? 0),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.balance(),
        });
    }, [queryClient, networkId]);

    const run = useCallback(
        (
            key: string,
            operation: string,
            successText: string,
            action: (api: SpeedBackend) => Promise<unknown>,
        ) => {
            if (!backend) {
                toast.error(
                    'Your session is still connecting. Try again in a moment.',
                );
                return;
            }
            setPendingKey(key);
            const api = backend;
            action(api)
                .then(() => {
                    toast.success(successText, { duration: 6000 });
                })
                .catch((error: unknown) => {
                    // never the backend's own words: the shared mapping turns a
                    // ValidationError code into something a trader can act on.
                    toastError(error, { operation });
                })
                .finally(() => {
                    setPendingKey(null);
                    // on failure just as much as on success: the row may be gone
                    // (already filled, already cancelled) and the lists are what
                    // say what the user actually holds now.
                    refreshAfterAction();
                });
        },
        [backend, refreshAfterAction],
    );

    if (isError || !data) return null;
    const { positions, orders } = data;
    if (positions.length === 0 && orders.length === 0) return null;

    const busy = pendingKey !== null;

    return (
        <section
            role="status"
            aria-label="Untracked exposure"
            className={cn(
                'rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5',
                className,
            )}
        >
            <div className="flex items-start gap-2">
                <AlertTriangle
                    size={14}
                    className="mt-0.5 shrink-0 text-amber-400"
                />
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-400">
                        Funds held outside your tracked trades
                    </p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Hyperliquid is holding {fmtUsd(data.heldUsd)} for a
                        position or order that is not shown in your trades.
                        Close or cancel it to free the funds.
                    </p>
                </div>
            </div>

            <div className="mt-2.5 flex flex-col gap-1.5">
                {positions.map((position) => {
                    const key = `position:${position.coin}`;
                    return (
                        <ExposureRow
                            key={key}
                            coin={position.coin}
                            side={position.side}
                            negative={position.side === 'SHORT'}
                            detail={positionDetail(position)}
                            amount={fmtUsd(position.marginUsd)}
                            amountLabel="Margin"
                            actionLabel="Close position"
                            pending={pendingKey === key}
                            disabled={busy}
                            onAction={() =>
                                run(
                                    key,
                                    'Close position',
                                    'Position closed',
                                    (api) =>
                                        api.closeTrade({
                                            coin: position.coin,
                                        }),
                                )
                            }
                        />
                    );
                })}
                {orders.map((order) => {
                    const key = `order:${order.coin}:${order.oid}`;
                    return (
                        <ExposureRow
                            key={key}
                            coin={order.coin}
                            side={order.side}
                            negative={order.side === 'SELL'}
                            detail={orderDetail(order)}
                            amount={fmtUsd(order.notionalUsd)}
                            amountLabel="Order value"
                            actionLabel="Cancel order"
                            pending={pendingKey === key}
                            disabled={busy}
                            onAction={() =>
                                run(
                                    key,
                                    'Cancel order',
                                    'Order cancelled',
                                    (api) =>
                                        api.cancelUntrackedOrder({
                                            coin: order.coin,
                                            oid: order.oid,
                                        }),
                                )
                            }
                        />
                    );
                })}
            </div>
        </section>
    );
};

export default UntrackedExposureCard;
